import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { register, registerWithImage } from "../../actions/authAction";
import TextError from "../../components/WraapperComponent/TextError";
import LeagalDisclaimerPopup from "./LegalDisclaimerPopup";
import SubscriptionInfoPopup from "./SubscriptionInfoPopup";
import useQuery from "./useQuery";
import { statesList } from "../../constants/states";
import FileUploadComponent from "../../components/FileUploadComponent";
import { getUserDetail } from "../../utils/helper";
const PractitionerForm = ({ setShowSuccessSection, setUser }) => {
  const dispatch = useDispatch();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const formikRef = useRef();
  let query = useQuery();
  let data = query.get("query");
  let queryData = null;
  if (data) {
    queryData = JSON.parse(atob(decodeURIComponent(data)));
  }

  const initialValues = {
    prefix: queryData ? queryData.prefix : "",
    first_name: queryData ? queryData.first_name : "",
    last_name: queryData ? queryData.last_name : "",
    user_email: queryData ? queryData.user_email : "",
    user_name: queryData ? queryData.user_name : "",
    phone: queryData ? queryData.phone : "",
    address: queryData ? queryData.address : "",
    city: queryData ? queryData.city : "",
    state: queryData ? queryData.state : "",
    zip: queryData ? queryData.zip : "",
    password: "",
    confirm_password: "",
    license_id: queryData ? queryData.license_id : "",
    license_agency: queryData ? queryData.license_agency : "",
    referred_by_id: queryData ? queryData.referral_code : "",
    license_description: null,
    license_state: "",
    file: null,
    customer_id: "",
    disclaimer: false,
  };

  const validationSchema = Yup.object({
    prefix: Yup.string().nullable(),
    first_name: Yup.string().nullable().required("This field is required"),
    last_name: Yup.string().nullable().required("This field is required"),
    phone: Yup.string()
      .nullable()
      .min(10, "Phone number must be 10 digits long")
      .max(10, "Phone number must be 10 digits long")
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Phone number is not valid"
      )
      .required("This field is required"),
    address: Yup.string().nullable().required("This field is required"),
    city: Yup.string().nullable().required("This field is required"),
    state: Yup.string().nullable().required("This field is required"),
    zip: Yup.string()
      .nullable()
      .max(5)
      .min(5)
      .matches(/^\d{5}$/, "Zip code is not valid")
      .required("This field is required"),
    user_email: Yup.string()
      .nullable()
      .email("Please provide a valid email address")
      .required("This field is required"),
    user_name: Yup.string()
      .nullable()
      .matches(
        /^[a-zA-Z0-9_.-]*$/,
        "Only letters and numbers, 2 to 20 characters"
      )
      .min(2, "Only letters and numbers, 2 to 64 characters")
      .max(64, "Only letters and numbers, 2 to 64 characters")
      .required("This field is required"),
    password: Yup.string()
      .nullable()
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]*$/,
        "Password must contain a minimum 8 characters with at least 1 number and 1 letter"
      )
      .min(
        8,
        "Password must contain a minimum 8 characters with at least 1 number and 1 letter"
      )
      .required("This field is required"),
    confirm_password: Yup.string()
      .nullable()
      .oneOf([Yup.ref("password"), null], "Confirm password do not matched")
      .required("This field is required"),
    license_id: Yup.string()
      .nullable()
      .matches(
        /^[a-zA-Z0-9_.-]*$/,
        "Only letters and numbers, 2 to 20 characters"
      )
      .min(2, "Only letters and numbers, 2 to 20 characters")
      .max(20, "Only letters and numbers, 2 to 20 characters")
      .required("This field is required"),
    license_agency: Yup.string().nullable().required("This field is required"),
    referred_by_id: Yup.string().nullable(),
    license_state: Yup.string()
      .when("license_agency", {
        is: (value) => value === "STATE",
        then: Yup.string().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    license_description: Yup.string()
      .when("license_agency", {
        is: (value) => value === "OT",
        then: Yup.string().nullable().required("This field is required"),
        otherwise: Yup.string(),
      })
      .nullable(),
    file: Yup.mixed()
      .required("Document is required")
      .test(
        "fileSize",
        "File size must be less than 5MB",
        (value) => value && value.size <= 5 * 1024 * 1024
      )
      .test(
        "fileType",
        "Invalid file type. Please upload an image",
        (value) => value && value.type.startsWith("image/")
      ),
    customer_id: Yup.string().nullable(),
    disclaimer: Yup.boolean()
      .oneOf([true], "You must accept the disclaimer to proceed.")
      .required("You must accept the disclaimer to proceed."),
  });

  const onSubmit = (values, { resetForm }) => {
    let formData = new FormData();

    formData.append("action", "registerAsNewPractitioner");
    formData.append("file", values.file);
    formData.append("prefix", JSON.stringify({ value: values.prefix }));
    formData.append("first_name", values.first_name?.trim());
    formData.append("last_name", values.last_name?.trim());
    formData.append("license_id", values.license_id);
    formData.append(
      "license_agency",
      values.license_agency === "STATE"
        ? values.license_state
        : values.license_agency
    );
    formData.append(
      "license_description",
      values.license_agency === "OT" ? values.license_description : ""
    );
    formData.append("user_email", values.user_email?.trim());
    formData.append("phone", values.phone);
    formData.append("user_name", values.user_name?.trim());
    formData.append("password", values.password);
    formData.append("referral_code", values.referred_by_id);
    formData.append("customer_id", values.customer_id);
    formData.append("address", values.address);
    formData.append("city", values.city);
    formData.append("state", values.state);
    formData.append("zip", values.zip);
    if (subscriptionData) {
      formData.append("subscription_data[ccName]", subscriptionData.nameOncard);
      formData.append(
        "subscription_data[ccNumber]",
        subscriptionData.cardNumber
      );
      formData.append(
        "subscription_data[ccExpMonth]",
        subscriptionData.expirationMonth
      );
      formData.append(
        "subscription_data[ccExpYear]",
        subscriptionData.expirationYear
      );
      formData.append(
        "subscription_data[expiration]",
        subscriptionData.expirationMonth + subscriptionData.expirationYear
      );
      formData.append(
        "subscription_data[first_name]",
        subscriptionData.firstname?.trim()
      );
      formData.append(
        "subscription_data[last_name]",
        subscriptionData.lastname?.trim()
      );
      formData.append(
        "subscription_data[email]",
        subscriptionData.email?.trim()
      );
      formData.append(
        "subscription_data[address_1]",
        subscriptionData.address_1
      );
      formData.append(
        "subscription_data[address_2]",
        subscriptionData.address_2
      );
      formData.append("subscription_data[city]", subscriptionData.city);
      formData.append("subscription_data[state]", subscriptionData.state);
      formData.append("subscription_data[phone]", subscriptionData.phone);
      formData.append("subscription_data[zip]", subscriptionData.zip);
      formData.append(
        "subscription_data[subscription_id]",
        subscriptionData.subscription_id
      );
    }
    dispatch(
      registerWithImage(formData, () => {
        setUser({
          prefix: values.prefix,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.user_email,
        });
        setShowSuccessSection(true);
        resetForm();
      })
    );
  };

  const handleExternalChange = (value) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("disclaimer", value); // Or false, depending on your logic
    }
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <h5 className="title">Practitioner Registration Form</h5>
        {showDisclaimer && (
          <LeagalDisclaimerPopup
            practitioner
            closePopup={() => setShowDisclaimer(false)}
            handleAgree={setAcceptDisclaimer}
            handleExternalChange={handleExternalChange}
          />
        )}
        {showSubscriptionPopup && (
          <SubscriptionInfoPopup
            practitioner
            closePopup={() => setShowSubscriptionPopup(false)}
            roleId={1}
            setSubscriptionData={setSubscriptionData}
          />
        )}
        <div className="form-section">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isValid,
              dirty,
              form,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <Form>
                  <div className="row">
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.prefix && touched.prefix ? "hasError" : null
                          }`}
                      >
                        <select
                          name="prefix"
                          defaultValue=""
                          onBlur={handleBlur}
                          value={values.prefix}
                          onChange={handleChange}
                        >
                          <option value="" disabled>
                            SELECT PREFIX
                          </option>
                          <option value="Mr">Mr</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Ms">Ms</option>
                          <option value="Dr">Dr</option>
                          <option value="Prof">Prof</option>
                        </select>
                      </div>
                      <ErrorMessage name="prefix" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.first_name && touched.first_name
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="first_name"
                          placeholder="First Name*"
                        />
                      </div>
                      <ErrorMessage name="first_name" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.last_name && touched.last_name
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.last_name}
                          onChange={handleChange}
                          name="last_name"
                          onBlur={handleBlur}
                          placeholder="Last Name*"
                        />
                      </div>
                      <ErrorMessage name="last_name" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.license_id && touched.license_id
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          onKeyDown={() => {
                            if (values.license_id?.length > 1) {
                              setFieldTouched("license_id");
                            }
                          }}
                          type="text"
                          value={values.license_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="license_id"
                          placeholder="License Number*"
                        />
                      </div>
                      <ErrorMessage name="license_id" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.license_agency && touched.license_agency
                            ? "hasError"
                            : null
                          }`}
                      >
                        <select
                          name="license_agency"
                          defaultValue=""
                          value={values.license_agency}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          <option value="" disabled>
                            SELECT ISSUING BODY*
                          </option>
                          <option value="NT">NCCAOM-National</option>
                          <option value="STATE">State</option>
                          <option value="OT">Other</option>
                        </select>
                      </div>
                      <ErrorMessage
                        name="license_agency"
                        component={TextError}
                      />
                    </div>
                    {values.license_agency === "STATE" && (
                      <div className="col-sm-12 pl-5">
                        <div
                          className={`form-wrapper ${errors.license_state && touched.license_state
                              ? "hasError"
                              : null
                            }`}
                        >
                          <select
                            name="license_state"
                            defaultValue=""
                            value={values.license_state}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          >
                            <option value="" disabled>
                              SELECT ISSUING STATE*
                            </option>
                            {statesList.map((option) => {
                              return (
                                <option key={option.value} value={option.value}>
                                  {option.key}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <ErrorMessage
                          name="license_state"
                          component={TextError}
                        />
                      </div>
                    )}
                    {values.license_agency === "OT" && (
                      <div className="col-sm-12  pl-5">
                        <div
                          className={`form-wrapper ${errors.license_description &&
                              touched.license_description
                              ? "hasError"
                              : null
                            }`}
                        >
                          <input
                            type="text"
                            value={values.license_description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="license_description"
                            placeholder="Description *"
                          />
                        </div>
                        <ErrorMessage
                          name="license_description"
                          component={TextError}
                        />
                      </div>
                    )}
                    <div className="col-sm-12">
                      <FileUploadComponent
                        setFieldTouched={setFieldTouched}
                        setFieldValue={setFieldValue}
                      />
                      <ErrorMessage name="file" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.user_email && touched.user_email
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          vali
                          type="text"
                          value={values.user_email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="user_email"
                          placeholder="Email*"
                        />
                      </div>
                      <ErrorMessage name="user_email" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.phone && touched.phone ? "hasError" : null
                          }`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          value={values.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, "");
                            setFieldValue("phone", val);
                          }}
                          onBlur={handleBlur}
                          name="phone"
                          maxLength={10}
                          placeholder="Phone Number*"
                          onPaste={(e) => {
                            const str = e.clipboardData.getData("Text");
                            let newStr = str.replace(/[^0-9]/g, "");
                            if (newStr.length > 10 && newStr[0] == 1) {
                              newStr = newStr.slice(1);
                            }
                            if (str !== newStr) {
                              e.preventDefault();
                              setFieldValue("phone", newStr);
                            }
                          }}
                        />
                      </div>
                      <ErrorMessage name="phone" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.address && touched.address ? "hasError" : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="address"
                          placeholder="Street*"
                        />
                      </div>
                      <ErrorMessage name="address" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.city && touched.city ? "hasError" : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.city}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="city"
                          placeholder="City*"
                        />
                      </div>
                      <ErrorMessage name="city" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.state && touched.state ? "hasError" : null
                          }`}
                      >
                        <select
                          value={values.state}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="state"
                          placeholder="State*"
                        >
                          <option value="" disabled>
                            SELECT STATE*
                          </option>

                          {statesList.map((option) => {
                            return (
                              <option key={option.value} value={option.value}>
                                {option.key}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <ErrorMessage name="state" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.zip && touched.zip ? "hasError" : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.zip}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={5}
                          name="zip"
                          placeholder="Zip*"
                        />
                      </div>
                      <ErrorMessage name="zip" component={TextError} />
                    </div>
                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper ${errors.user_name && touched.user_name
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          type="text"
                          value={values.user_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="user_name"
                          placeholder="Username*"
                        />
                      </div>
                      <ErrorMessage name="user_name" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper password-field ${errors.password && touched.password
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          type={showPassword ? "text" : "password"}
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="password"
                          placeholder="Password*"
                        />
                        <div className="pass-icon">
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            color="gray"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </div>
                      </div>
                      <ErrorMessage name="password" component={TextError} />
                    </div>

                    <div className="col-sm-12">
                      <div
                        className={`form-wrapper password-field ${errors.confirm_password && touched.confirm_password
                            ? "hasError"
                            : null
                          }`}
                      >
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={values.confirm_password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="confirm_password"
                          placeholder="Repeat Password*"
                        />
                        <div className="pass-icon">
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                            color="gray"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          />
                        </div>
                      </div>
                      <ErrorMessage
                        name="confirm_password"
                        component={TextError}
                      />
                    </div>
                    <div className="col-sm-12">
                      <div className="form-wrapper">
                        <input
                          type="text"
                          value={values.customer_id}
                          maxLength={20}
                          onChange={handleChange}
                          name="customer_id"
                          placeholder="Customer Id"
                        />
                      </div>
                      <p className="small">
                        If you are a Sunten customer from the past, kindly
                        provide your customer ID.
                      </p>
                    </div>
                    {/* <div className="col-sm-12">
                      <div className="form-wrapper">
                        <input
                          type="text"
                          value={values.referred_by_id}
                          onChange={handleChange}
                          name="referred_by_id"
                          placeholder="Referral Code"
                        />
                      </div>
                      <p className="small">
                        If you have been given a special Referral Code you can
                        add it here in order to give credit to the person who
                        referred you to us.
                      </p>
                    </div> */}

                    <div className="col-sm-12 mt-1">
                      <label
                        style={{ marginLeft: 0 }}
                        className="container"
                        onClick={() => setShowDisclaimer(true)}
                      >
                        LEGAL DISCLAIMERS
                        <input
                          type="checkbox"
                          checked={acceptDisclaimer}
                          name="disclaimer"
                        />
                        <span className="checkmark"></span>
                      </label>
                      <ErrorMessage name="disclaimer" component={TextError} />
                    </div>
                  </div>
                  <div className="submit-section mt-2">
                    <button
                      className="btn-orange"
                      type="submit"
                    //  disabled={!(isValid && dirty && acceptDisclaimer)}
                    >
                      Register
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
      <div className="col-md-6">
        <h5 className="title">Practitioner Benefits Include:</h5>
        <ul>
          <li>Commission on All Herb Orders</li>
          <li>Prescription Builder</li>
          <li>Formula Writing</li>
          <li>Conversion Calculator</li>
          <li>Patient and Order Management</li>
          <li>Suggest Dosages</li>
          <li>Order Fulfillment</li>
        </ul>
      </div>
    </div>
  );
};
export default PractitionerForm;
